import { supabase } from '../../supabase.js';
import { validateAndGetTenantUuid } from '../../utils/tenantUtils.js';
import { ServiceError } from '../../utils/errorHandler.js';

class AppInstallationService {
  constructor() {
    this.installationsTable = 'app_installations';
    this.userAccessTable = 'user_app_access';
    this.tokenUsageTable = 'token_usage_logs';
  }

  /**
   * Install an app for a user
   */
  async installApp(userId, appId, tenant_id, installationSource = 'appstore') {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      // Check if app exists and is active
      const { data: app, error: appError } = await supabase
        .from('apps')
        .select('*')
        .eq('id', appId)
        .eq('app_status', 'active')
        .single();

      if (appError || !app) {
        throw new ServiceError('APP_NOT_FOUND', 'App not found or not available');
      }

      // Check if already installed
      const { data: existingInstallation, error: checkError } = await supabase
        .from(this.installationsTable)
        .select('*')
        .eq('user_id', userId)
        .eq('app_id', appId)
        .eq('is_active', true)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        throw new ServiceError('INSTALLATION_CHECK_FAILED', 'Failed to check existing installation', checkError);
      }

      if (existingInstallation) {
        throw new ServiceError('APP_ALREADY_INSTALLED', 'App is already installed');
      }

      // Create installation record
      const { data: installation, error: installError } = await supabase
        .from(this.installationsTable)
        .insert({
          user_id: userId,
          app_id: appId,
          tenant_id: tenantUuid,
          installation_source: installationSource,
          is_active: true
        })
        .select()
        .single();

      if (installError) {
        throw new ServiceError('INSTALLATION_FAILED', 'Failed to install app', installError);
      }

      // Create user app access record
      const { error: accessError } = await supabase
        .from(this.userAccessTable)
        .insert({
          user_id: userId,
          app_id: appId,
          tenant_id: tenantUuid,
          access_level: 'basic',
          tokens_allocated: app.free_tier_limit || 10,
          tokens_remaining: app.free_tier_limit || 10,
          is_active: true
        });

      if (accessError) {
        throw new ServiceError('ACCESS_CREATION_FAILED', 'Failed to create app access', accessError);
      }

      // Update app download count
      const { error: updateError } = await supabase
        .from('apps')
        .update({ total_downloads: app.total_downloads + 1 })
        .eq('id', appId);

      if (updateError) {
        console.warn('Failed to update app download count:', updateError);
      }

      return {
        success: true,
        data: installation,
        message: 'App installed successfully'
      };
    } catch (error) {
      throw error instanceof ServiceError ? error : new ServiceError('INSTALLATION_FAILED', 'Failed to install app', error);
    }
  }

  /**
   * Uninstall an app
   */
  async uninstallApp(userId, appId, tenant_id) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      // Update installation record
      const { data, error } = await supabase
        .from(this.installationsTable)
        .update({
          is_active: false,
          uninstall_date: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('app_id', appId)
        .eq('is_active', true)
        .select()
        .single();

      if (error) {
        throw new ServiceError('UNINSTALLATION_FAILED', 'Failed to uninstall app', error);
      }

      // Deactivate user app access
      const { error: accessError } = await supabase
        .from(this.userAccessTable)
        .update({ is_active: false })
        .eq('user_id', userId)
        .eq('app_id', appId)
        .eq('is_active', true);

      if (accessError) {
        console.warn('Failed to deactivate app access:', accessError);
      }

      return {
        success: true,
        data,
        message: 'App uninstalled successfully'
      };
    } catch (error) {
      throw error instanceof ServiceError ? error : new ServiceError('UNINSTALLATION_FAILED', 'Failed to uninstall app', error);
    }
  }

  /**
   * Get user's installed apps
   */
  async getUserInstalledApps(userId, tenant_id) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      const { data, error } = await supabase
        .from(this.installationsTable)
        .select(`
          *,
          apps!inner(
            id,
            app_name,
            app_description,
            app_category,
            app_icon_url,
            app_version,
            rating,
            total_reviews
          ),
          user_app_access!inner(
            tokens_allocated,
            tokens_used,
            tokens_remaining,
            access_level
          )
        `)
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('installation_date', { ascending: false });

      if (error) {
        throw new ServiceError('INSTALLED_APPS_FETCH_FAILED', 'Failed to fetch installed apps', error);
      }

      return {
        success: true,
        data: data || [],
        message: 'Installed apps retrieved successfully'
      };
    } catch (error) {
      throw error instanceof ServiceError ? error : new ServiceError('INSTALLED_APPS_FETCH_FAILED', 'Failed to fetch installed apps', error);
    }
  }

  /**
   * Track app usage and consume tokens
   */
  async trackAppUsage(userId, appId, action, tokensConsumed, tenant_id, sessionId = null, usageDetails = {}) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      // Check if user has access to the app
      const { data: access, error: accessError } = await supabase
        .from(this.userAccessTable)
        .select('*')
        .eq('user_id', userId)
        .eq('app_id', appId)
        .eq('is_active', true)
        .single();

      if (accessError || !access) {
        throw new ServiceError('APP_ACCESS_DENIED', 'User does not have access to this app');
      }

      // Check if user has enough tokens
      if (access.tokens_remaining < tokensConsumed) {
        throw new ServiceError('INSUFFICIENT_TOKENS', 'Insufficient tokens for this action');
      }

      // Calculate cost
      const { data: app } = await supabase
        .from('apps')
        .select('base_token_cost')
        .eq('id', appId)
        .single();

      const costAmount = tokensConsumed * (app?.base_token_cost || 1);

      // Log token usage
      const { error: usageError } = await supabase
        .from(this.tokenUsageTable)
        .insert({
          user_id: userId,
          app_id: appId,
          tenant_id: tenantUuid,
          session_id: sessionId,
          action_type: action,
          tokens_consumed: tokensConsumed,
          usage_details: usageDetails,
          cost_amount: costAmount
        });

      if (usageError) {
        throw new ServiceError('USAGE_TRACKING_FAILED', 'Failed to track app usage', usageError);
      }

      // Update user's remaining tokens
      const { error: updateError } = await supabase
        .from(this.userAccessTable)
        .update({
          tokens_used: access.tokens_used + tokensConsumed,
          tokens_remaining: access.tokens_remaining - tokensConsumed,
          last_used_at: new Date().toISOString()
        })
        .eq('id', access.id);

      if (updateError) {
        throw new ServiceError('TOKEN_UPDATE_FAILED', 'Failed to update token balance', updateError);
      }

      // Update installation usage count
      const { error: installUpdateError } = await supabase
        .from(this.installationsTable)
        .update({
          usage_count: supabase.sql`usage_count + 1`,
          last_used_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('app_id', appId)
        .eq('is_active', true);

      if (installUpdateError) {
        console.warn('Failed to update installation usage count:', installUpdateError);
      }

      return {
        success: true,
        data: {
          tokens_consumed: tokensConsumed,
          tokens_remaining: access.tokens_remaining - tokensConsumed,
          cost_amount: costAmount
        },
        message: 'App usage tracked successfully'
      };
    } catch (error) {
      throw error instanceof ServiceError ? error : new ServiceError('USAGE_TRACKING_FAILED', 'Failed to track app usage', error);
    }
  }

  /**
   * Get user's app usage statistics
   */
  async getUserAppUsage(userId, appId, tenant_id, dateRange = null) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      let query = supabase
        .from(this.tokenUsageTable)
        .select('*')
        .eq('user_id', userId)
        .eq('app_id', appId);

      if (dateRange) {
        query = query.gte('created_at', dateRange.start)
                    .lte('created_at', dateRange.end);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        throw new ServiceError('USAGE_STATS_FETCH_FAILED', 'Failed to fetch usage statistics', error);
      }

      // Calculate statistics
      const totalTokens = data?.reduce((sum, usage) => sum + usage.tokens_consumed, 0) || 0;
      const totalCost = data?.reduce((sum, usage) => sum + (usage.cost_amount || 0), 0) || 0;
      const usageCount = data?.length || 0;

      return {
        success: true,
        data: {
          usage_logs: data || [],
          statistics: {
            total_tokens_consumed: totalTokens,
            total_cost: totalCost,
            usage_count: usageCount,
            average_tokens_per_use: usageCount > 0 ? totalTokens / usageCount : 0
          }
        },
        message: 'Usage statistics retrieved successfully'
      };
    } catch (error) {
      throw error instanceof ServiceError ? error : new ServiceError('USAGE_STATS_FETCH_FAILED', 'Failed to fetch usage statistics', error);
    }
  }

  /**
   * Check if user has access to an app
   */
  async checkAppAccess(userId, appId, tenant_id) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      const { data, error } = await supabase
        .from(this.userAccessTable)
        .select('*')
        .eq('user_id', userId)
        .eq('app_id', appId)
        .eq('is_active', true)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw new ServiceError('ACCESS_CHECK_FAILED', 'Failed to check app access', error);
      }

      return {
        success: true,
        data: {
          has_access: !!data,
          access_details: data || null
        },
        message: 'App access check completed'
      };
    } catch (error) {
      throw error instanceof ServiceError ? error : new ServiceError('ACCESS_CHECK_FAILED', 'Failed to check app access', error);
    }
  }
}

export const appInstallationService = new AppInstallationService();
